import RefreshIcon from "@assets/icons/refresh.svg";
import CustomButton from "@components/buttons/CustomButton";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import ToggleInput from "@components/inputs/ToggleInput";
import SlidingPanel from "@components/sliders/SlidingPanel";
import { primeServices } from "@services/apis/Prime";
import {
  CircuitBreakerConfigData,
  CircuitBreakerServiceConfig,
  CircuitBreakerServiceName,
  UpdateCircuitBreakerConfigRequest,
} from "@services/models/Prime/CircuitBreakerConfigModel";
import { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";

const SERVICES: { id: CircuitBreakerServiceName; label: string }[] = [
  { id: "cashIn", label: "Cash-In" },
  { id: "cashOut", label: "Cash-Out" },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const INTEGER_PATTERN = /^\d+$/;
const PERCENT_PATTERN = /^\d+(\.\d{1,2})?$/;
const POLL_MS = 15000;

interface ServiceForm {
  lookbackMinutes: string;
  minCompletedTrades: string;
  warnPercent: string;
  criticalPercent: string;
  autoSwitchOffEnabled: boolean;
  autoExtendDeadlineEnabled: boolean;
  criticalExtendMinutes: string;
  warnExtendMinutes: string;
  processingLookbackMinutes: string;
  processingThreshold: string;
  processingExtendMinutes: string;
  slackEnabled: boolean;
  emailEnabled: boolean;
  alertCooldownMinutes: string;
  muteRemainingMinutes: string;
}

interface SharedForm {
  slackWebhookUrl: string;
  alertEmails: string;
}

const INTEGER_FIELDS: {
  key: keyof ServiceForm;
  label: string;
  min: number;
  hint: string;
}[] = [
  {
    key: "lookbackMinutes",
    label: "Lookback (min)",
    min: 1,
    hint: "Window used for the success rate.",
  },
  {
    key: "minCompletedTrades",
    label: "Min completed trades",
    min: 1,
    hint: "Sample size required before the rate is judged.",
  },
  {
    key: "warnExtendMinutes",
    label: "Warn extend (min)",
    min: 0,
    hint: "Added to pending deadlines below the warn rate.",
  },
  {
    key: "criticalExtendMinutes",
    label: "Critical extend (min)",
    min: 0,
    hint: "Added to pending deadlines below the critical rate.",
  },
  {
    key: "processingLookbackMinutes",
    label: "Processing lookback (min)",
    min: 1,
    hint: "Window for stuck or in-progress trades.",
  },
  {
    key: "processingThreshold",
    label: "Processing threshold",
    min: 1,
    hint: "Pending trades that trigger a congestion alert.",
  },
  {
    key: "processingExtendMinutes",
    label: "Processing extend (min)",
    min: 0,
    hint: "Added to deadlines when processing is above the threshold.",
  },
  {
    key: "alertCooldownMinutes",
    label: "Alert cooldown (min)",
    min: 0,
    hint: "0 uses the monitor default of 15 minutes.",
  },
];

const isFlagOn = (value: number | boolean) => value === 1 || value === true;

const asFieldValue = (value: number | string | null | undefined) =>
  value === null || value === undefined ? "" : String(value);

const toForm = (service: CircuitBreakerServiceConfig): ServiceForm => ({
  lookbackMinutes: asFieldValue(service.lookbackMinutes),
  minCompletedTrades: asFieldValue(service.minCompletedTrades),
  warnPercent: asFieldValue(service.warnPercent),
  criticalPercent: asFieldValue(service.criticalPercent),
  autoSwitchOffEnabled: isFlagOn(service.autoSwitchOffEnabled),
  autoExtendDeadlineEnabled: isFlagOn(service.autoExtendDeadlineEnabled),
  criticalExtendMinutes: asFieldValue(service.criticalExtendMinutes),
  warnExtendMinutes: asFieldValue(service.warnExtendMinutes),
  processingLookbackMinutes: asFieldValue(service.processingLookbackMinutes),
  processingThreshold: asFieldValue(service.processingThreshold),
  processingExtendMinutes: asFieldValue(service.processingExtendMinutes),
  slackEnabled: isFlagOn(service.slackEnabled),
  emailEnabled: isFlagOn(service.emailEnabled),
  alertCooldownMinutes: asFieldValue(service.alertCooldownMinutes),
  muteRemainingMinutes: asFieldValue(service.muteRemainingMinutes),
});

const toShared = (data: CircuitBreakerConfigData): SharedForm => ({
  slackWebhookUrl: asFieldValue(
    data.notifications?.slackWebhookUrl ?? data.services.cashIn?.slackWebhookUrl,
  ),
  alertEmails: asFieldValue(
    data.notifications?.alertEmails ?? data.services.cashIn?.alertEmails,
  ),
});

const formsFromConfig = (data: CircuitBreakerConfigData) => ({
  cashIn: toForm(data.services.cashIn),
  cashOut: toForm(data.services.cashOut),
});

const flag = (value: boolean): 0 | 1 => (value ? 1 : 0);

const getApiErrorMessage = (error: unknown, fallback: string) => {
  const err = error as {
    response?: { data?: { msg?: string } };
    message?: string;
  };
  return err?.response?.data?.msg || err?.message || fallback;
};

const formatCount = (value: number | string | undefined) => {
  const num = Number(value);
  return Number.isFinite(num) ? String(num) : "--";
};

const formatWhen = (value: string | null | undefined) => {
  if (!value) return "Not snoozed";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const normalizeEmails = (value: string) =>
  value
    .split(",")
    .map((email) => email.trim())
    .filter(Boolean)
    .join(",");

const validateShared = (shared: SharedForm) => {
  const errors: Record<string, string> = {};
  const webhook = shared.slackWebhookUrl.trim();
  if (!webhook) {
    errors.slackWebhookUrl = "A Slack webhook URL is required.";
  } else if (!/^https:\/\/.+/i.test(webhook)) {
    errors.slackWebhookUrl = "Enter an https Slack webhook URL.";
  }

  const emails = normalizeEmails(shared.alertEmails);
  if (!emails) {
    errors.alertEmails = "At least one alert email is required.";
  } else if (
    emails.split(",").some((email) => !EMAIL_PATTERN.test(email))
  ) {
    errors.alertEmails = "Enter valid comma-separated email addresses.";
  }

  return errors;
};

const validateService = (form: ServiceForm) => {
  const errors: Record<string, string> = {};

  INTEGER_FIELDS.forEach((field) => {
    const value = String(form[field.key]).trim();
    if (!INTEGER_PATTERN.test(value) || Number(value) < field.min) {
      errors[field.key] =
        field.min === 0
          ? `${field.label} must be a whole number of 0 or more.`
          : `${field.label} must be a whole number of at least ${field.min}.`;
    }
  });

  (["warnPercent", "criticalPercent"] as const).forEach((key) => {
    const value = form[key].trim();
    const num = Number(value);
    if (!PERCENT_PATTERN.test(value) || num < 0 || num > 100) {
      errors[key] = "Enter a percent from 0 to 100, with at most 2 decimals.";
    }
  });

  if (!errors.warnPercent && !errors.criticalPercent) {
    if (Number(form.warnPercent) <= Number(form.criticalPercent)) {
      errors.warnPercent = "Warn percent must be higher than critical percent.";
    }
  }

  return errors;
};

const buildRequest = (
  data: CircuitBreakerConfigData,
  service: CircuitBreakerServiceName,
  form: ServiceForm,
  shared: SharedForm,
  muteNotificationsUntil: string | null,
): UpdateCircuitBreakerConfigRequest => ({
  provider: data.provider || data.services[service].provider || "ntx",
  service,
  lookbackMinutes: Number(form.lookbackMinutes),
  minCompletedTrades: Number(form.minCompletedTrades),
  warnPercent: Number(form.warnPercent),
  criticalPercent: Number(form.criticalPercent),
  autoSwitchOffEnabled: flag(form.autoSwitchOffEnabled),
  autoExtendDeadlineEnabled: flag(form.autoExtendDeadlineEnabled),
  criticalExtendMinutes: Number(form.criticalExtendMinutes),
  warnExtendMinutes: Number(form.warnExtendMinutes),
  processingLookbackMinutes: Number(form.processingLookbackMinutes),
  processingThreshold: Number(form.processingThreshold),
  processingExtendMinutes: Number(form.processingExtendMinutes),
  slackEnabled: flag(form.slackEnabled),
  emailEnabled: flag(form.emailEnabled),
  alertCooldownMinutes: Number(form.alertCooldownMinutes),
  slackWebhookUrl: shared.slackWebhookUrl.trim(),
  alertEmails: normalizeEmails(shared.alertEmails),
  muteNotificationsUntil,
});

const requestFromServer = (
  data: CircuitBreakerConfigData,
  service: CircuitBreakerServiceName,
  muteNotificationsUntil: string | null,
) =>
  buildRequest(
    data,
    service,
    toForm(data.services[service]),
    toShared(data),
    muteNotificationsUntil,
  );

const hasBothServices = (data?: CircuitBreakerConfigData | null) =>
  Boolean(data?.services?.cashIn && data?.services?.cashOut);

interface NtxCircuitBreakerPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const NtxCircuitBreakerPanel = ({
  isOpen,
  onClose,
}: NtxCircuitBreakerPanelProps) => {
  const { handleError, handleSuccess } = useSnackbarMessage();
  const handleErrorRef = useRef(handleError);
  handleErrorRef.current = handleError;
  const saveInFlight = useRef(false);
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  const [config, setConfig] = useState<CircuitBreakerConfigData | null>(null);
  const [forms, setForms] = useState<Record<
    CircuitBreakerServiceName,
    ServiceForm
  > | null>(null);
  const [shared, setShared] = useState<SharedForm>({
    slackWebhookUrl: "",
    alertEmails: "",
  });
  const [activeService, setActiveService] =
    useState<CircuitBreakerServiceName>("cashIn");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [muting, setMuting] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    saveInFlight.current = false;

    const load = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const response = await primeServices.getCircuitBreakerConfig();
        if (cancelled) return;
        if (!hasBothServices(response.data)) {
          throw new Error(
            "Circuit breaker config is missing Cash-In or Cash-Out.",
          );
        }
        setConfig(response.data);
        setForms(formsFromConfig(response.data));
        setShared(toShared(response.data));
        setErrors({});
      } catch (err) {
        if (!cancelled) {
          setLoadError(
            getApiErrorMessage(err, "Failed to load NTX circuit breaker config"),
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    const intervalId = window.setInterval(async () => {
      if (saveInFlight.current) return;
      try {
        const response = await primeServices.getCircuitBreakerConfig({
          silent: true,
        });
        if (!cancelled && hasBothServices(response.data)) {
          setConfig(response.data);
        }
      } catch {
        // Keep the last snapshot on a quiet refresh failure.
      }
    }, POLL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [isOpen, reloadKey]);

  const refreshConfig = async () => {
    setRefreshing(true);
    try {
      const response = await primeServices.getCircuitBreakerConfig({
        silent: true,
      });
      if (!hasBothServices(response.data)) return;
      setConfig(response.data);
      setForms(formsFromConfig(response.data));
      setShared(toShared(response.data));
    } catch (err) {
      handleError({
        message: getApiErrorMessage(err, "Failed to refresh NTX monitor"),
      });
    } finally {
      setRefreshing(false);
    }
  };

  const reseedService = (data: CircuitBreakerConfigData) => {
    setConfig(data);
    setForms(formsFromConfig(data));
    setShared(toShared(data));
  };

  const updateForm = (
    service: CircuitBreakerServiceName,
    patch: Partial<ServiceForm>,
  ) => {
    setForms((prev) =>
      prev ? { ...prev, [service]: { ...prev[service], ...patch } } : prev,
    );
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(patch).forEach((key) => delete next[key]);
      return next;
    });
  };

  const handleSave = async () => {
    if (!config || !forms) return;
    const form = forms[activeService];
    const nextErrors = {
      ...validateShared(shared),
      ...validateService(form),
    };
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const muteNotificationsUntil =
      config.services[activeService]?.muteNotificationsUntil ?? null;
    setSaving(true);
    saveInFlight.current = true;
    try {
      const response = await primeServices.updateCircuitBreakerConfig(
        buildRequest(
          config,
          activeService,
          form,
          shared,
          muteNotificationsUntil,
        ),
      );
      try {
        const fresh = await primeServices.getCircuitBreakerConfig({
          silent: true,
        });
        reseedService(fresh.data);
      } catch {
        if (response.data?.services) {
          reseedService(response.data);
        }
      }
      handleSuccess({
        message:
          response.msg ||
          `${activeService === "cashIn" ? "Cash-In" : "Cash-Out"} configuration updated`,
      });
    } catch (err) {
      handleError({
        message: getApiErrorMessage(err, "Failed to update NTX configuration"),
      });
    } finally {
      saveInFlight.current = false;
      setSaving(false);
    }
  };

  const applyMute = async (
    targets: CircuitBreakerServiceName[],
    muteNotificationsUntil: string | null,
    actionKey: string,
  ) => {
    if (!config) return;
    setMuting(actionKey);
    saveInFlight.current = true;
    try {
      let message = "";
      for (const service of targets) {
        const response = await primeServices.updateCircuitBreakerConfig(
          requestFromServer(config, service, muteNotificationsUntil),
        );
        message = response.msg;
      }
      try {
        const fresh = await primeServices.getCircuitBreakerConfig({
          silent: true,
        });
        if (hasBothServices(fresh.data)) {
          reseedService(fresh.data);
        }
      } catch {
        // The mute request succeeded; the next poll will catch up.
      }
      handleSuccess({
        message:
          message ||
          (muteNotificationsUntil
            ? "Notifications snoozed"
            : "Notifications unmuted"),
      });
    } catch (err) {
      handleError({
        message: getApiErrorMessage(err, "Failed to update notification snooze"),
      });
      try {
        const fresh = await primeServices.getCircuitBreakerConfig({
          silent: true,
        });
        if (hasBothServices(fresh.data)) {
          reseedService(fresh.data);
        }
      } catch {
        // Leave the last known config in place.
      }
    } finally {
      saveInFlight.current = false;
      setMuting(null);
    }
  };

  const handleMute = () => {
    const minutes = (
      forms?.[activeService]?.muteRemainingMinutes ?? ""
    ).trim();
    if (
      !INTEGER_PATTERN.test(minutes) ||
      Number(minutes) < 1 ||
      Number(minutes) > 10080
    ) {
      setErrors((prev) => ({
        ...prev,
        muteMinutes: "Enter a snooze from 1 to 10080 minutes.",
      }));
      return;
    }
    setErrors((prev) => {
      const next = { ...prev };
      delete next.muteMinutes;
      return next;
    });
    const until = new Date(
      Date.now() + Number(minutes) * 60 * 1000,
    ).toISOString();
    applyMute([activeService], until, `mute-${activeService}`);
  };

  const serviceConfig = config?.services?.[activeService];
  const form = forms?.[activeService];
  const busy = saving || muting !== null;
  const totalTrades = Number(serviceConfig?.metrics?.totalTradesInWindow);
  const successRate = Number(serviceConfig?.metrics?.currentSuccessRate);
  const processing = Number(serviceConfig?.metrics?.currentProcessingTrades);
  const hasSample =
    serviceConfig != null &&
    Number.isFinite(totalTrades) &&
    totalTrades >= Number(serviceConfig.minCompletedTrades);
  const rateTone = !hasSample
    ? "text-text-base"
    : successRate < Number(serviceConfig?.criticalPercent)
      ? "text-text-status-red"
      : successRate < Number(serviceConfig?.warnPercent)
        ? "text-text-status-yellow"
        : "text-text-status-green";
  const healthLabel = !serviceConfig
    ? "--"
    : !hasSample
      ? "Waiting for sample"
      : successRate < Number(serviceConfig.criticalPercent)
        ? "Critical"
        : successRate < Number(serviceConfig.warnPercent)
          ? "Warning"
          : "Healthy";
  const congested =
    serviceConfig != null &&
    Number.isFinite(processing) &&
    processing >= Number(serviceConfig.processingThreshold);

  return (
    <SlidingPanel
      isOpen={isOpen}
      heading="NTX Monitor"
      onClose={onClose}
      onClickCross={onClose}
      showConfirm={false}
      isMobile={isMobile}
    >
      <div className="p-3 flex flex-col gap-4 max-h-[80vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3">
          <p className="text-[12px] text-text-subtle leading-5">
            Live Cash-In and Cash-Out health for NTX. Each save updates one
            service. Slack webhook and alert emails are shared across both.
          </p>
          <button
            type="button"
            title="Refresh NTX monitor"
            aria-label="Refresh NTX monitor"
            disabled={loading || refreshing}
            onClick={refreshConfig}
            className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border-subtle disabled:opacity-50"
          >
            <img
              src={RefreshIcon}
              alt=""
              className={refreshing ? "animate-spin" : ""}
            />
          </button>
        </div>

        {loading && (
          <p className="text-[12px] text-text-subtle">Loading NTX monitor...</p>
        )}

        {!loading && loadError && (
          <div className="flex flex-col gap-2">
            <p className="text-[12px] text-text-status-red">{loadError}</p>
            <button
              type="button"
              onClick={() => setReloadKey((key) => key + 1)}
              className="h-8 w-fit rounded-md border border-border-subtle px-3 text-[12px] font-semibold text-text-base"
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !loadError && config && form && serviceConfig && (
          <>
            <section className="flex flex-col gap-3 rounded-lg border border-border-subtle bg-bg-base px-3 py-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[13px] font-semibold text-text-base">
                  Notifications
                </p>
                <span
                  className={`rounded px-2 py-0.5 text-[10px] font-semibold ${
                    config.notifications?.isMuted
                      ? "bg-bg-status-yellow text-text-base"
                      : "bg-bg-status-green text-text-status-green"
                  }`}
                >
                  {config.notifications?.isMuted
                    ? `Muted ${config.notifications.muteRemainingMinutes}m`
                    : "Alerts active"}
                </span>
              </div>
              <p className="text-[11px] text-text-subtle leading-4">
                Webhook and alert emails apply to both services. Snooze Cash-In
                or Cash-Out separately below.
              </p>

              <GlobalTextInputField
                title="Slack webhook"
                value={shared.slackWebhookUrl}
                placeholder="https://hooks.slack.com/services/..."
                onChange={(event) => {
                  setShared((prev) => ({
                    ...prev,
                    slackWebhookUrl: event.target.value,
                  }));
                  setErrors((prev) => {
                    const next = { ...prev };
                    delete next.slackWebhookUrl;
                    return next;
                  });
                }}
              />
              {errors.slackWebhookUrl && (
                <p className="text-[11px] text-text-status-red">
                  {errors.slackWebhookUrl}
                </p>
              )}
              <GlobalTextInputField
                title="Alert emails"
                value={shared.alertEmails}
                placeholder="ops@tylt.money, liqops@tylt.money"
                onChange={(event) => {
                  setShared((prev) => ({
                    ...prev,
                    alertEmails: event.target.value,
                  }));
                  setErrors((prev) => {
                    const next = { ...prev };
                    delete next.alertEmails;
                    return next;
                  });
                }}
              />
              <p className="text-[11px] text-text-subtle">
                Comma-separated. Saved with the service you update.
              </p>
              {errors.alertEmails && (
                <p className="text-[11px] text-text-status-red">
                  {errors.alertEmails}
                </p>
              )}
            </section>

            <div className="grid grid-cols-2 gap-2">
              {SERVICES.map((service) => (
                <button
                  key={service.id}
                  type="button"
                  onClick={() => setActiveService(service.id)}
                  className={`h-8 rounded-md border text-[12px] font-semibold ${
                    activeService === service.id
                      ? "border-border-button-primary bg-bg-base text-text-base"
                      : "border-border-subtle bg-bg-page-body text-text-subtle"
                  }`}
                >
                  {service.label}
                </button>
              ))}
            </div>

            <section className="grid grid-cols-2 gap-2">
              <Metric
                label="Success rate"
                value={
                  Number.isFinite(successRate) ? `${successRate}%` : "--"
                }
                detail={healthLabel}
                valueClassName={rateTone}
              />
              <Metric
                label="Completed in window"
                value={`${formatCount(serviceConfig.metrics?.successfulTradesInWindow)} / ${formatCount(serviceConfig.metrics?.totalTradesInWindow)}`}
                detail={
                  hasSample
                    ? `Min sample ${serviceConfig.minCompletedTrades}`
                    : `Need ${serviceConfig.minCompletedTrades} completed`
                }
              />
              <Metric
                label="Processing"
                value={formatCount(serviceConfig.metrics?.currentProcessingTrades)}
                detail={`Threshold ${serviceConfig.processingThreshold}`}
                valueClassName={
                  congested ? "text-text-status-red" : "text-text-base"
                }
              />
              <Metric
                label="Service alerts"
                value={
                  serviceConfig.isMuted
                    ? `Muted ${serviceConfig.muteRemainingMinutes}m`
                    : "Active"
                }
                detail={formatWhen(serviceConfig.muteNotificationsUntil)}
                valueClassName={
                  serviceConfig.isMuted
                    ? "text-text-status-yellow"
                    : "text-text-status-green"
                }
              />
            </section>

            <section className="flex flex-col gap-1 rounded-lg border border-border-subtle bg-bg-base px-3 py-2">
              <ToggleRow
                label="Slack alerts"
                hint="Send Slack messages for this service."
                checked={form.slackEnabled}
                disabled={busy}
                onToggle={() =>
                  updateForm(activeService, { slackEnabled: !form.slackEnabled })
                }
              />
              <ToggleRow
                label="Email alerts"
                hint="Send email for this service."
                checked={form.emailEnabled}
                disabled={busy}
                onToggle={() =>
                  updateForm(activeService, { emailEnabled: !form.emailEnabled })
                }
              />
              <ToggleRow
                label="Auto shut-off"
                hint={
                  activeService === "cashOut"
                    ? "Off keeps Cash-Out running and only raises alerts."
                    : "On turns the service off when the rate drops below critical."
                }
                checked={form.autoSwitchOffEnabled}
                disabled={busy}
                onToggle={() =>
                  updateForm(activeService, {
                    autoSwitchOffEnabled: !form.autoSwitchOffEnabled,
                  })
                }
              />
              <ToggleRow
                label="Auto-extend deadlines"
                hint="Extend pending trades while performance is degraded."
                checked={form.autoExtendDeadlineEnabled}
                disabled={busy}
                onToggle={() =>
                  updateForm(activeService, {
                    autoExtendDeadlineEnabled: !form.autoExtendDeadlineEnabled,
                  })
                }
              />
              <div className="flex flex-col gap-2 pt-2">
                <GlobalTextInputField
                  title={`Snooze ${activeService === "cashIn" ? "Cash-In" : "Cash-Out"} (minutes)`}
                  type="number"
                  allowDecimal={false}
                  value={form.muteRemainingMinutes}
                  onChange={(event) => {
                    const value = event.target.value;
                    if (value === "" || INTEGER_PATTERN.test(value)) {
                      updateForm(activeService, {
                        muteRemainingMinutes: value,
                      });
                      setErrors((current) => {
                        const next = { ...current };
                        delete next.muteMinutes;
                        return next;
                      });
                    }
                  }}
                />
                {errors.muteMinutes && (
                  <p className="text-[11px] text-text-status-red">
                    {errors.muteMinutes}
                  </p>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <SecondaryButton
                    text="Mute this service"
                    disabled={busy}
                    loading={muting === `mute-${activeService}`}
                    onClick={handleMute}
                  />
                  <SecondaryButton
                    text="Unmute service"
                    disabled={busy}
                    loading={muting === `unmute-${activeService}`}
                    onClick={() =>
                      applyMute(
                        [activeService],
                        null,
                        `unmute-${activeService}`,
                      )
                    }
                  />
                </div>
              </div>
            </section>

            <section className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <GlobalTextInputField
                  title="Warn percent"
                  type="number"
                  value={form.warnPercent}
                  onChange={(event) => {
                    const value = event.target.value;
                    if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
                      updateForm(activeService, { warnPercent: value });
                    }
                  }}
                />
                {errors.warnPercent && (
                  <p className="text-[11px] text-text-status-red">
                    {errors.warnPercent}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <GlobalTextInputField
                  title="Critical percent"
                  type="number"
                  value={form.criticalPercent}
                  onChange={(event) => {
                    const value = event.target.value;
                    if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
                      updateForm(activeService, { criticalPercent: value });
                    }
                  }}
                />
                {errors.criticalPercent && (
                  <p className="text-[11px] text-text-status-red">
                    {errors.criticalPercent}
                  </p>
                )}
              </div>
              {INTEGER_FIELDS.map((field) => (
                <div key={field.key} className="flex flex-col gap-1">
                  <GlobalTextInputField
                    title={field.label}
                    type="number"
                    allowDecimal={false}
                    value={String(form[field.key])}
                    onChange={(event) => {
                      const value = event.target.value;
                      if (value === "" || INTEGER_PATTERN.test(value)) {
                        updateForm(activeService, {
                          [field.key]: value,
                        } as Partial<ServiceForm>);
                      }
                    }}
                  />
                  {errors[field.key] ? (
                    <p className="text-[11px] text-text-status-red">
                      {errors[field.key]}
                    </p>
                  ) : (
                    <p className="text-[11px] text-text-subtle">{field.hint}</p>
                  )}
                </div>
              ))}
            </section>

            <p className="text-[10px] text-text-subtle">
              Last updated {formatWhen(serviceConfig.updatedAt)} by{" "}
              {serviceConfig.updatedBy || "--"}
            </p>

            <CustomButton
              text={`Save ${activeService === "cashIn" ? "Cash-In" : "Cash-Out"}`}
              isActive={!busy}
              isButtonLoading={saving}
              onClick={handleSave}
            />
          </>
        )}
      </div>
    </SlidingPanel>
  );
};

const ToggleRow = ({
  label,
  hint,
  checked,
  disabled,
  onToggle,
}: {
  label: string;
  hint: string;
  checked: boolean;
  disabled?: boolean;
  onToggle: () => void;
}) => (
  <div className="flex items-center justify-between gap-3 border-b border-border-subtle py-2 last:border-b-0">
    <div>
      <p className="text-[12px] font-medium text-text-base">{label}</p>
      <p className="text-[10px] leading-4 text-text-subtle">{hint}</p>
    </div>
    <div className={disabled ? "pointer-events-none opacity-50" : ""}>
      <ToggleInput isToggled={checked} onToggle={onToggle} size="sm" />
    </div>
  </div>
);

const Metric = ({
  label,
  value,
  detail,
  valueClassName = "text-text-base",
}: {
  label: string;
  value: string;
  detail: string;
  valueClassName?: string;
}) => (
  <div className="rounded-md border border-border-subtle bg-bg-base px-2 py-2">
    <p className="text-[10px] text-text-subtle">{label}</p>
    <p className={`text-[14px] font-semibold ${valueClassName}`}>{value}</p>
    <p className="truncate text-[10px] text-text-subtle">{detail}</p>
  </div>
);

const SecondaryButton = ({
  text,
  onClick,
  disabled,
  loading,
}: {
  text: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
}) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className="h-8 rounded-md border border-border-subtle bg-bg-page-body px-2 text-[11px] font-semibold text-text-base disabled:opacity-50"
  >
    {loading ? "Saving..." : text}
  </button>
);

export default NtxCircuitBreakerPanel;
